import { postPayInfo } from '@/services/plan';
import { EnumHttp } from '@/enum/http';
import { PayCode } from '@/types/payment';
import type { PaymentError, PaymentResult, PaymentTransactionParams } from '@/types/payment';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

interface PaymentPollingOptions {
  scope: 'recharge' | 'plan' | 'myPlan' | 'package' | 'historyOrder';
  params: PaymentTransactionParams;
  enabled: boolean;
  refetchInterval: number;
  onFinish: (message: string | undefined, isSuccess: boolean) => void;
}

const usePaymentPolling = ({
  scope, params, enabled, refetchInterval, onFinish,
}: PaymentPollingOptions) => {
  const instanceId = useId();
  const [session, setSession] = useState(0);
  const [isStopped, setIsStopped] = useState(false);
  const sessionRef = useRef(0);
  const mountedRef = useRef(true);
  const stoppedRef = useRef(false);
  const activeRef = useRef(false);
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const startSession = useCallback(() => {
    stoppedRef.current = false;
    setIsStopped(false);
    sessionRef.current += 1;
    setSession(sessionRef.current);
  }, []);
  const stopSession = useCallback(() => {
    // 立即屏蔽旧回包，同时禁用查询和轮询定时器。
    stoppedRef.current = true;
    if (mountedRef.current) setIsStopped(true);
  }, []);
  const isCurrentSession = useCallback(
    () => mountedRef.current && sessionRef.current === session, [session],
  );

  const active = enabled && session > 0 && !isStopped;
  activeRef.current = active;
  const query = useQuery<PaymentResult, PaymentError>({
    queryKey: ['paymentTransaction', scope, instanceId, session, params],
    queryFn: () => postPayInfo(params, {
      handlePaymentErrorLocally: true,
      isPaymentRequestCurrent: () => mountedRef.current && activeRef.current &&
        !stoppedRef.current && sessionRef.current === session,
    }),
    enabled: active,
    refetchInterval,
    retry: false,
    gcTime: 0,
  });

  useEffect(() => {
    if (!active || !mountedRef.current || stoppedRef.current || sessionRef.current !== session) return;

    // 错误发生时不读取缓存中的旧成功数据。
    const result = query.error ? query.error.info : query.data;
    const code = result?.code;
    // 网络错误和登录失效仍交给全局处理；2222 保留弹窗并继续轮询。
    if (code === undefined || code === PayCode.Waiting ||
      code === EnumHttp.NEED_LOGIN || code === EnumHttp.TOKEN_EXPIRE) return;

    // 先停止，保证同一支付会话只处理一次结束结果。
    stopSession();
    const message = result?.message ?? query.error?.message;
    onFinishRef.current(message, code === PayCode.Success);
  }, [active, session, query.data, query.error, query.errorUpdatedAt, stopSession]);

  return {
    startSession,
    stopSession,
    isCurrentSession,
  };
};

export default usePaymentPolling;
